import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { RouteName } from "constant/routeNames";

// Icons
import { FiMonitor, FiDownload, FiFileText } from "react-icons/fi";

// Existing components
import SubscriptionService from "services/subscription/subscriptionService";
import { ActivePackages, Transection } from "services/subscription/types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination } from "swiper";
import { toast } from "react-toastify";
import { displayTimeFormat } from "utils/helperFuncs";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatShortDate = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatMemberSince = (dateStr: string) => {
  if (!dateStr) return "";
  return new Date(dateStr)
    .toLocaleDateString("en-US", { month: "long", year: "numeric" })
    .toUpperCase();
};

const PAGE_SIZE = 10;

// ─── Tab Types ───────────────────────────────────────────────────────────────

type Tab = "overview" | "paid-subscriptions" | "payment-history";

// ─── Overview Tab ────────────────────────────────────────────────────────────

const OverviewTab = () => {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState<ActivePackages[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<ActivePackages | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const formatDate = (date?: string) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleCancleSubscription = async () => {
    if (!selectedPackage) return null;
    setCancelLoading(true);
    try {
      const resp = await SubscriptionService.cancleSubscriptionPackge(
        selectedPackage.id,
      );
      if (resp) {
        toast.success(
          `${selectedPackage.title} ${resp.message}` ||
            "Subscription cancelled. Access continues until the end of your billing period.",
        );
      }

      const response = await SubscriptionService.getAllPurchasedPackages();
      setSubscriptions(response);
      const updatedSelected = response.find(
        (pkg) => pkg.id === selectedPackage.id,
      );
      if (updatedSelected) {
        setSelectedPackage(updatedSelected);
      } else if (response.length > 0) {
        setSelectedPackage(response[0]);
      } else {
        setSelectedPackage(null);
      }
      setShowCancelModal(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Failed to cancel subscription. Please try again.",
      );
    } finally {
      setCancelLoading(false);
    }
  };

  useEffect(() => {
    SubscriptionService.getAllPurchasedPackages()
      .then((response: ActivePackages[]) => {
        setSubscriptions(response);
        if (response.length > 0) {
          setSelectedPackage(response[0]);
        }
      })
      .catch(() => {
        setSubscriptions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-gray-700 rounded-full border-t-red-500 animate-spin" />
      </div>
    );
  }

  const dayBefore = (date: string | number | Date) => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    return d.toISOString();
  };

  if (subscriptions.length === 0) {
    return (
      <div className="max-w-2xl">
        <div className="rounded-2xl bg-[#141414] border border-[#242424] p-8 flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#1e1e1e] border border-[#2a2a2a] flex items-center justify-center">
            <FiMonitor size={24} className="text-gray-400" />
          </div>

          <h3 className="text-xl font-semibold text-white">No Active Plan</h3>

          <p className="text-sm text-gray-400">
            You don't have an active subscription.
          </p>

          <button
            onClick={() => navigate(RouteName.SubscriptionPlanRoute)}
            className="mt-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#e30030] to-[#c70025] text-white text-sm font-medium"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Top Section */}
      <div className="grid grid-cols-1 lg:grid-cols-[650px_1fr] gap-6">
        {/* Left Side */}

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-white">Current Plan</h3>
          {subscriptions.map((pkg) => (
            <div
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg)}
              className={`cursor-pointer rounded-2xl border p-5 transition-all ${
                selectedPackage?.id === pkg.id
                  ? "border-red-500 bg-[#1a1a1a]"
                  : "border-[#242424] bg-[#141414]"
              }`}
            >
              <div className="flex items-start gap-4">
                {pkg.thumbnail && (
                  <img
                    src={pkg.thumbnail}
                    alt={pkg.title}
                    className="object-cover w-12 h-12 rounded-lg"
                  />
                )}

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">
                      {pkg.title} Plan
                    </h3>
                    <span
                      className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: "#E6604720",
                        color: "#E66047",
                      }}
                    >
                      ${pkg.amount}/{displayTimeFormat(pkg.plan_period_format)}
                    </span>
                  </div>

                  <p className="mt-2 text-sm text-gray-400 line-clamp-3">
                    {pkg.description}
                  </p>

                  <div className="flex items-center gap-4 mt-3 text-sm">
                    <span
                      className={`capitalize ${
                        pkg.status === "active"
                          ? "text-green-400"
                          : "text-red-400"
                      }`}
                    >
                      {pkg.status}
                    </span>
                    <span className="text-gray-400">
                      Since {formatDate(pkg.purchased_formatted_date)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Side */}
        <div className="rounded-2xl bg-[#141414] border border-[#242424] p-6">
          <h3 className="mb-6 text-xl font-semibold text-white">
            Subscription Details
          </h3>

          {selectedPackage && (
            <>
              <div className="space-y-5">
                {/* <div>
                  <p className="text-sm text-gray-500">Subscription ID</p>
                  <p className="text-white">{selectedPackage.id}</p>
                </div> */}

                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p
                    className={`capitalize ${
                      selectedPackage.status === "active"
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {selectedPackage.status}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="text-white">
                    {formatDate(selectedPackage.purchased_formatted_date)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Cancel Date</p>
                  <p className="text-white">
                    {formatDate(dayBefore(selectedPackage.current_period_end))}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Cancel At Period End</p>
                  <p className="text-white">
                    {selectedPackage.cancel_at_period_end ? "Yes" : "No"}
                  </p>
                </div>

                {selectedPackage.canceled_at && (
                  <div>
                    <p className="text-sm text-gray-500">Cancelled At</p>
                    <p className="text-red-400">
                      {formatDate(selectedPackage.canceled_at)}
                    </p>
                  </div>
                )}

                {/* <div>
                  <p className="text-sm text-gray-500">Entitlement ID</p>
                  <p className="text-white break-all">
                    {selectedPackage.entitlement_id}
                  </p>
                </div> */}
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                {/* <button
                  onClick={() => navigate(RouteName.SubscriptionPlanRoute)}
                  className="px-5 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Change Plan
                </button> */}

                <button
                  className={`px-5 py-2.5 rounded-lg border ${
                    selectedPackage?.canceled_at
                      ? "cursor-not-allowed border-gray-600 text-gray-500 opacity-50"
                      : "cursor-pointer border-red-500 text-red-400 hover:bg-red-500/10"
                  }`}
                  onClick={() => {
                    setShowCancelModal(true);
                  }}
                  aria-disabled={!!selectedPackage?.canceled_at}
                  disabled={!!selectedPackage?.canceled_at}
                >
                  Cancel Subscription
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Carousel */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-white">
          Your Subscriptions
        </h3>

        <Swiper
          slidesPerView={4}
          spaceBetween={16}
          modules={[Pagination]}
          breakpoints={{
            0: { slidesPerView: 1 },
            450: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1200: { slidesPerView: 4 },
          }}
          className="mySwiper"
        >
          {subscriptions.map((pkg) => (
            <SwiperSlide key={pkg.id}>
              <div
                onClick={() => setSelectedPackage(pkg)}
                className={`cursor-pointer rounded-2xl border p-3 h-full transition-all ${
                  selectedPackage?.id === pkg.id
                    ? "border-red-500 bg-[#1a1a1a]"
                    : "border-[#242424] bg-[#141414]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {pkg.thumbnail && (
                      <img
                        src={pkg.thumbnail}
                        alt={pkg.title}
                        className="object-cover w-10 h-10 rounded-lg"
                      />
                    )}
                    <h4 className="font-semibold text-white">
                      {pkg.title} Plan
                    </h4>
                  </div>

                  <span
                    className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium"
                    style={{
                      backgroundColor: "#E6604720",
                      color: "#E66047",
                    }}
                  >
                    ${pkg.amount}/{displayTimeFormat(pkg.plan_period_format)}
                  </span>
                </div>

                <p className="mt-3 text-sm">
                  <span
                    className={`capitalize ${
                      pkg.status === "active"
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {pkg.status}
                  </span>
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  Since {formatDate(pkg.purchased_formatted_date)}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#141414] border border-[#242424] rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="mb-3 text-xl font-semibold text-white">
              Cancel Subscription
            </h3>
            <p className="mb-6 text-gray-400">
              your content only available untill valid subscription period only
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-5 py-2.5 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
                disabled={cancelLoading}
              >
                Close
              </button>
              <button
                onClick={handleCancleSubscription}
                className="px-5 py-2.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center min-w-[100px]"
                disabled={cancelLoading}
              >
                {cancelLoading ? (
                  <div className="w-5 h-5 border-2 rounded-full border-white/30 border-t-white animate-spin" />
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Transactions Tab ─────────────────────────────────────────────────────────

const TransactionsTab = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setIsLoading(true);
    SubscriptionService.getSubscriptionTransection()
      .then((res: any) => {
        setTransactions(res || []);
      })
      .catch(() => setTransactions([]))
      .finally(() => setIsLoading(false));
  }, []);

  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);
  const paginatedRows = transactions.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleExportCSV = () => {
    if (transactions.length === 0) return;
    const header = ["Date", "Description", "Amount", "Status", "Order Number"];
    const rows = transactions.map((t: any) => [
      formatShortDate(t.purchased_formatted_date),
      t.content_name,
      `$${t.amount}`,
      t.payment_status,
      t.order_number,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "billing-history.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusStyle = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "paid" || s === "completed" || s === "succeeded")
      return "bg-green-500/10 text-green-400 border border-green-500/20";
    if (s === "expired")
      return "bg-red-500/10 text-red-400 border border-red-500/20";
    if (s === "refunded")
      return "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20";
    return "bg-gray-500/10 text-gray-400 border border-gray-500/20";
  };

  const getStatusLabel = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "paid") return "COMPLETED";
    return status?.toUpperCase();
  };

  const getDescription = (t: any) => {
    const formatDate = (dateStr: string, offsetDays = 0) => {
      const d = new Date(dateStr);
      d.setDate(d.getDate() + offsetDays);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const dateperiod =
      t.current_period_start && t.current_period_end
        ? `Membership for ${formatDate(t.current_period_start)} - ${formatDate(t.current_period_end, -1)}`
        : "Unknown";

    return { date_period: dateperiod };
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Payment History</h2>
        <button
          onClick={handleExportCSV}
          disabled={transactions.length === 0}
          className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FiDownload size={15} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="rounded-xl bg-[#141414] border border-[#242424] overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-4 px-6 py-5 border-b border-[#1f1f1f] last:border-0"
            >
              <div className="h-3 w-24 bg-[#222] animate-pulse rounded" />
              <div className="flex-1 h-3 bg-[#222] animate-pulse rounded" />
              <div className="h-3 w-16 bg-[#222] animate-pulse rounded" />
              <div className="h-6 w-24 bg-[#222] animate-pulse rounded-full" />
              <div className="h-8 w-8 bg-[#222] animate-pulse rounded-lg" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && transactions.length === 0 && (
        <div className="flex flex-col items-center gap-3 mt-16">
          <p className="text-sm text-gray-500">No transactions found.</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && transactions.length > 0 && (
        <div className="rounded-2xl bg-[#141414] border border-[#242424] overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[130px_1fr_120px_100px_120px_60px] gap-4 px-6 py-3 bg-[#111] border-b border-[#1f1f1f]">
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Date
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Description
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Plan Name
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Amount
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Status
            </span>
            <span className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              Receipt
            </span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-[#1a1a1a]">
            {paginatedRows.map((t: Transection, i: number) => {
              const desc = getDescription(t);
              return (
                <div
                  key={i}
                  className="grid grid-cols-[130px_1fr_120px_100px_120px_60px] gap-4 px-6 py-4 items-center hover:bg-[#191919] transition-colors"
                >
                  {/* Date */}
                  <span className="text-sm text-gray-300">
                    {formatShortDate(t.created_at)}
                  </span>

                  {/* Description */}
                  <div>
                    <p className="text-sm font-medium text-white">
                      {desc.date_period}
                    </p>
                  </div>

                  {/* Plan name */}
                  <span className="text-sm font-medium text-white">
                    {t.title}
                  </span>

                  {/* Amount */}
                  <span className="text-sm font-medium text-white">
                    ${t.amount}
                  </span>

                  {/* Status */}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide w-fit ${getStatusStyle(t.status)}`}
                  >
                    {getStatusLabel(t.status)}
                  </span>

                  {/* Receipt */}
                  <Link
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1e1e1e] border border-[#2a2a2a] text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
                    to={t.invoice_link}
                    target="_blank"
                  >
                    <FiFileText size={14} />
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#1f1f1f]">
            <span className="text-xs text-gray-500">
              Showing{" "}
              {Math.min((currentPage - 1) * PAGE_SIZE + 1, transactions.length)}
              –{Math.min(currentPage * PAGE_SIZE, transactions.length)} of{" "}
              {transactions.length} transactions
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Member Page ────────────────────────────────────────────────────────

const MemberPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview", label: "Overview" },
    // { id: "paid-subscriptions", label: "Paid Subscription" },
    { id: "payment-history", label: "Payment History" },
  ];

  return (
    <>
      <Helmet>
        <title>Membership - AYOZAT</title>
        <meta
          name="description"
          content="Manage your AYOZAT membership, subscriptions, and billing history."
        />
        <link rel="canonical" href={`${FE_URL}/membership`} />
        <meta property="og:title" content="Membership - AYOZAT" />
        <meta
          property="og:description"
          content="Manage your AYOZAT membership, subscriptions, and billing history."
        />
        <meta property="og:image" content="https://ayozat.co.uk/logo192.png" />
        <meta property="og:url" content={`${FE_URL}/membership`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen px-4 py-8 text-white">
        {/* Tab bar */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? "bg-[#e30030] text-white"
                  : "bg-transparent border border-[#3a3a3a] text-white hover:border-gray-500"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="transition-all duration-200">
          {activeTab === "overview" && <OverviewTab />}
          {/* {activeTab === "paid-subscriptions" && <PaidSubscriptionsTab />} */}
          {activeTab === "payment-history" && <TransactionsTab />}
        </div>
      </div>
    </>
  );
};

export default MemberPage;
